import * as React from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import { Tooltip, Typography } from '@mui/material'
import { blue } from '@mui/material/colors'

import { AdminUITheme } from 'types/AdminUiThemeMui'
import i18n from 'i18n'
import type { Participant as ParticipantType } from 'types/Participants'
import getIcon from 'lib/utility/GetIcon'
import { getKey } from 'lib/participants/Helper'
import 'moment/locale/fr-ca'
import { COLUMNS } from 'lib/GlobalConstants'
import { getParticipantActions } from 'components/Participants/ParticipantActions'
import { useAppSelector } from 'redux/Hook'

type RecordType = {
  row: ParticipantType
}

const linkStyles = {
  textDecoration: 'none',
  color: blue['800'],
  display: 'flex',
  width: '100%',
}

const columns = () => {
  const theme: AdminUITheme = useTheme()
  const features = useAppSelector((state) => state.features.features)

  const isActiveDirectorySignupEnabled =
    features['enable_active_directory_signup']?.enabled

  return [
    {
      field: 'id',
      headerName: 'id',
      width: 10,
      hide: true,
    },
    {
      field: 'login',
      headerName: 'name',
      flex: 1,
      minWidth: 250,
      renderCell: (record: RecordType) => {
        const {
          row: { login, id },
        } = record

        const profileURL = `/adm/participant/${id}`

        return (
          <Link
            to={profileURL}
            style={linkStyles}
            data-testid="participant-name"
          >
            <Typography variant="inherit" noWrap>
              {login}
            </Typography>
          </Link>
        )
      },
    },
    {
      field: 'email',
      headerName: 'email',
      minWidth: 250,
      flex: 1,
      renderCell: (record: RecordType) => {
        const {
          row: { email },
        } = record

        return (
          <Typography variant="inherit" noWrap width="auto">
            {email}
          </Typography>
        )
      },
    },
    {
      field: 'status',
      headerName: 'status',
      width: 82,
      sortable: false,
      renderCell: (record: RecordType) => {
        const participantStatus = getKey(record['row'])
        const icon = getIcon(participantStatus)
        const label = i18n.t(`participant.status.${participantStatus}`)

        return (
          <Box
            className="MuiBox-PRMGrid-Container"
            style={theme.ParticipantGrid.displayFlexWithCenter}
          >
            <Tooltip title={label}>{icon}</Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'lastSeenAt',
      headerName: 'lastSeen',
      type: 'date',
      minWidth: 190,
      renderCell: (record: RecordType) => {
        if (!record['row'].lastSeenAt) return i18n.t('participant.grid.notSeen')

        const date = new Date(record['row'].lastSeenAt)

        return moment(date, 'YYYYMMDD').locale(i18n.default.language).fromNow()
      },
    },
    {
      field: COLUMNS.createdAt,
      headerName: 'joinedDate',
      type: 'date',
      width: 134,
      renderCell: (record: RecordType) =>
        moment(record['row'].createdAt, 'YYYYMMDD')
          .locale(i18n.default.language)
          .format('DD MMM YYYY'),
    },
    {
      field: 'sso',
      headerName: 'sso',
      sortable: false,
      hide: !isActiveDirectorySignupEnabled,
      renderCell: (record: RecordType) => {
        return record['row'].viaSso ? i18n.t('common.yes') : i18n.t('common.no')
      },
    },
    {
      field: 'imported',
      headerName: 'imported',
      sortable: false,
      renderCell: (record: RecordType) =>
        record['row'].imported === true
          ? i18n.t('participant.imported.yes')
          : i18n.t('participant.imported.no'),
    },
    {
      field: 'actions',
      headerName: 'actions',
      type: 'actions',
      width: 120,
      align: 'left',
      getActions: (params: RecordType) => getParticipantActions(params.row),
    },
  ]
}

export default columns
